import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useReview } from '../../context/ReviewContext';
import ReviewList from '../Review/ReviewList';
import ReviewCard from '../Review/ReviewCard';
import { reviewModel } from '../../data/reviewModel';

const ReviewManagement = () => {
  const { currentUser } = useAuth();
  const { reviews, loadUserProfileReviews } = useReview();
  const [profileReviews, setProfileReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('received'); // received, given

  useEffect(() => {
    if (currentUser) {
      loadProfileReviews();
    }
  }, [currentUser]);

  const loadProfileReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('ReviewManagement: Loading reviews for user:', currentUser.uid);
      const reviewsData = await loadUserProfileReviews(currentUser.uid, 'girl');
      console.log('ReviewManagement: Raw reviews data from Firebase:', reviewsData);
      console.log('ReviewManagement: First review ID:', reviewsData[0]?.id);
      console.log('ReviewManagement: First review ID type:', typeof reviewsData[0]?.id);
      setProfileReviews(reviewsData);
    } catch (error) {
      console.error('Error loading profile reviews:', error);
      setError('เกิดข้อผิดพลาดในการโหลดรีวิว');
    } finally {
      setLoading(false);
    }
  };

  const receivedReviews = profileReviews.filter(review => 
    review.revieweeId === currentUser.uid
  );

  const givenReviews = reviews.filter(review => 
    review.reviewerId === currentUser.uid
  );

  const pendingReviews = receivedReviews.filter(review => 
    review.status === reviewModel.status.PENDING
  );

  const approvedReviews = receivedReviews.filter(review => 
    review.status === reviewModel.status.APPROVED
  );

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p>กำลังโหลดรีวิว...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-500 text-lg mb-2">⚠️</div>
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadProfileReviews}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          ลองใหม่
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">จัดการรีวิว</h1>
        <p className="text-gray-600">ดูและตอบกลับรีวิวที่ได้รับจากลูกค้า</p>
        
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow border p-4">
          <div className="flex items-center">
            <div className="text-2xl text-blue-500 mr-3">📥</div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{receivedReviews.length}</div>
              <div className="text-sm text-gray-600">รีวิวที่ได้รับ</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <div className="flex items-center">
            <div className="text-2xl text-yellow-500 mr-3">⏳</div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{pendingReviews.length}</div>
              <div className="text-sm text-gray-600">รออนุมัติ</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <div className="flex items-center">
            <div className="text-2xl text-green-500 mr-3">✅</div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{approvedReviews.length}</div>
              <div className="text-sm text-gray-600">อนุมัติแล้ว</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-4">
          <div className="flex items-center">
            <div className="text-2xl text-purple-500 mr-3">📤</div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{givenReviews.length}</div>
              <div className="text-sm text-gray-600">รีวิวที่ให้</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow border mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('received')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'received'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              รีวิวที่ได้รับ ({receivedReviews.length})
            </button>
            <button
              onClick={() => setActiveTab('given')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'given'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              รีวิวที่ให้ ({givenReviews.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'received' ? (
            <div className="space-y-4">
              {receivedReviews.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-4">⭐</div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">ยังไม่มีรีวิว</h3>
                  <p className="text-gray-500">รีวิวจากลูกค้าจะแสดงที่นี่</p>
                </div>
              ) : (
                receivedReviews.map((review) => {
                  console.log('ReviewManagement: Rendering review:', review);
                  console.log('ReviewManagement: Review ID:', review.id);
                  console.log('ReviewManagement: Review ID type:', typeof review.id);
                  return (
                    <ReviewCard
                      key={review.id}
                      review={review}
                      showResponse={true}
                      canRespond={true}
                    />
                  );
                })
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {givenReviews.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-4">📝</div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">ยังไม่ให้รีวิว</h3>
                  <p className="text-gray-500">รีวิวที่คุณให้จะแสดงที่นี่</p>
                </div>
              ) : (
                givenReviews.map((review) => (
                  <ReviewCard
                    key={review.id}
                    review={review}
                    showResponse={false}
                    canRespond={false}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewManagement;
